import { html, nothing, svg, type TemplateResult } from "lit";
import type { AppointmentOptions } from "../../config/types";
import type { AppointmentService } from "../data/mock-appointments";

const ICON_CARET = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
const ICON_IMAGE = svg`<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.6"/><path d="M21 15l-5-5L5 21"/></svg>`;

export interface AppointmentServiceViewModel {
  options: AppointmentOptions;
  expanded: boolean;
  thumbnailLoaded: boolean;
  thumbnailErrored: boolean;
  /** Empty when there is no price to state — the row then omits the price
   * slot rather than claiming a credit-priced service is free. */
  formattedPrice: string;
  onSelect: () => void;
  onToggleExpanded: () => void;
  onThumbnailLoaded: () => void;
  onThumbnailError: (image: HTMLImageElement) => void;
}

export function renderAppointmentService(
  service: AppointmentService,
  model: AppointmentServiceViewModel,
): TemplateResult {
  const hasDescription = !!service.description;
  const usableThumbnail =
    !!service.thumbnail && (model.thumbnailLoaded || !model.thumbnailErrored);

  return html`<div class="svc ${model.expanded ? "expanded" : ""}">
    <button class="svc-main" @click=${model.onSelect}>
      ${model.options.hideThumbnail
        ? nothing
        : usableThumbnail
          ? html`<img
              class="thumb"
              src=${service.thumbnail}
              alt=""
              @load=${model.onThumbnailLoaded}
              @error=${(event: Event) =>
                model.onThumbnailError(event.target as HTMLImageElement)}
            />`
          : html`<span class="thumb thumb-ph" aria-hidden="true"
              >${ICON_IMAGE}</span
            >`}
      <span style="min-width:0;flex:1">
        <span class="nm-row">
          ${hasDescription
            ? html`<button
                class="svc-exp ${model.expanded ? "open" : ""}"
                type="button"
                aria-expanded=${String(model.expanded)}
                aria-label="Show description"
                @click=${(event: Event) => {
                  event.stopPropagation();
                  model.onToggleExpanded();
                }}
              >
                ${ICON_CARET}
              </button>`
            : nothing}
          <span class="nm">${service.name}</span>
        </span>
        <span class="meta">${service.duration} min</span>
      </span>
    </button>
    ${model.options.hidePrices || !model.formattedPrice
      ? nothing
      : html`<span class="price">${model.formattedPrice}</span>`}
    ${model.expanded && hasDescription
      ? html`<div class="svc-desc">${service.description}</div>`
      : nothing}
  </div>`;
}
